'use client';

import React, { useState, useEffect } from 'react';
import { Box, Typography, Container, Paper, List, ListItem, ListItemText, useMediaQuery, useTheme } from '@mui/material';
import { SearchAndShareNavBar } from '../components/search_and_share_navbar';
import LargeFooter from '../components/large_footer';

export default function VenueTermsPage() {
    return (
        <Box sx={{ backgroundColor: '#1C1C34', color: 'white', minHeight: '100vh' }}>
            <SearchAndShareNavBar />
            <Typography variant="h3" component="h1" gutterBottom fontFamily='Roboto Condensed' sx={{ color: '#FFFFFF', textAlign: 'center', mb: 4, px: 4, mt: 2 }}>
                Get Litty, LLC and Venue Terms and Conditions
            </Typography>
            <Container maxWidth="md" sx={{ py: 4 }}>
                <Terms />
            </Container>
            <LargeFooter />
        </Box>
    );
}

const Terms = () => {
    return (
        <Paper elevation={3} sx={{ p: 4, backgroundColor: '#f5f5f5', color: '#333', flexGrow: 1 }}>
            <Typography variant="h5" gutterBottom fontFamily='Inter' sx={{ fontWeight: 'bold' }}>
                Billing Agreement
            </Typography>
            <Typography paragraph fontFamily='Inter'>
                This document outlines the billing agreement between Get Litty platform (getlitty.com)(hereinafter referred to as "Litty" or "the platform") and the Venue (referred to as "you" or "the Venue") for bookings made through the Litty platform. By using the Litty platform you acknowledge and agree that you have read, understood and agreed to these terms set out below and confirm your agreement to enter into and comply with a legally binding contract with Litty on these terms. If you do not agree to any part of these terms, you have no right to use the platform.
            </Typography>

            <Typography variant="h6" id="commission-structure" gutterBottom fontFamily='Inter' sx={{ fontWeight: 'bold', mt: 3 }}>
                1. Commission Structure
            </Typography>
            <Typography paragraph fontFamily='Inter'>
                1.1. In consideration for the use of the Litty platform to advertise your venue and for all bookings where payment is made through the platform or payment due to you from clients as a result of a booking through the platform, Litty shall receive a non-refundable commission of 15% .
            </Typography>
            <Typography paragraph fontFamily='Inter'>
                1.2. Litty does not charge the Venue a subscription or listing fee.
            </Typography>
            <Typography paragraph fontFamily='Inter'>
                1.3. Litty is entitled to a commission on any Booking where the initial contact between the Customer and the Venue Manager, regarding the booked Venue or Room, occurred through the Litty platform. This includes, but is not limited to, situations where:
            </Typography>
            <Typography component="div" fontFamily='Inter'>
                <List>
                    <ListItem>
                        <ListItemText primary="(a) The Customer first contacted the Venue Manager through the Litty platform;" />
                    </ListItem>
                    <ListItem>
                        <ListItemText primary="(b) When a Litty representative first attempts to get in touch with you on behalf of a customer whether by message, email, telephone or by any other means;" />
                    </ListItem>
                    <ListItem>
                        <ListItemText primary="(c) The Customer made a Booking Inquiry or Booking Request through the Litty platform." />
                    </ListItem>
                </List>
            </Typography>

            <Typography variant="h6" gutterBottom fontFamily='Inter' sx={{ fontWeight: 'bold', mt: 2 }}>
                1.4. Commission Exceptions
            </Typography>
            <Typography component="div" fontFamily='Inter'>
                <List>
                    <ListItem>
                        <ListItemText primary="(a) Pre-existing Customer Relationship: The Venue Manager provides written evidence that the Customer contacted or began negotiations with the Venue Manager regarding the same Booking before the Customer made a Booking Inquiry or Booking Request through the Litty platform, and the Venue Manager responded to the Customer during that pre-existing interaction." />
                    </ListItem>
                    <ListItem>
                        <ListItemText primary="(b) Booking Cancellation with Full Refund: The Booking is canceled by the Customer in accordance with Section 11 of these Terms, and the Customer receives a full refund." />
                    </ListItem>
                </List>
            </Typography>

            <Typography variant="h6" gutterBottom fontFamily='Inter' sx={{ fontWeight: 'bold', mt: 3 }}>
                2. Operating Models
            </Typography>
            <Typography paragraph fontFamily='Inter'>
                All rights and obligations in respect of payment of any commission are binding on both the Venue and Litty as well as any assignees and successors. Litty offers two operating models for billing:
            </Typography>
            <Typography variant="h6" gutterBottom fontFamily='Inter' sx={{ fontWeight: 'bold', mt: 2 }}>
                2.1. Deposit Model
            </Typography>
            <Typography paragraph fontFamily='Inter'>
                2.1.1. Litty will collect a 10-20% deposit from the client at the time of booking.
            </Typography>
            <Typography paragraph fontFamily='Inter'>
                2.1.2. The deposit will be refunded to the client on the day of the event.
            </Typography>
            <Typography paragraph fontFamily='Inter'>
                2.1.3. The Venue shall report the total amount spent by the client to Litty within 72 hours of the event's conclusion. Litty requires a valid copy of a receipt, bill or invoice or payment.
            </Typography>
            <Typography paragraph fontFamily='Inter'>
                2.1.4. Litty will invoice the Venue for 15% of the total amount spent by the client.
            </Typography>
            <Typography paragraph fontFamily='Inter'>
                2.1.5. The Venue will make payment in full within 15 days after an invoice is received.
            </Typography>
            <Typography paragraph fontFamily='Inter'>
                2.1.6. Instructions for making payment of commission will be specified in the invoice for a given booking.
            </Typography>
            <Typography paragraph fontFamily='Inter'>
                2.1.7. A failure to pay an invoice within 10 days of the due date may result in your venue page being suspended and your credit history being affected.
            </Typography>
            <Typography paragraph fontFamily='Inter'>
                2.1.8. In the event we reasonably incur any cost (debt collection costs, legal advice, legal proceeding) to enforce our rights as a result of your breach of this agreement, including but not limited to recovery of any sums due, you will reimburse us such reasonable expense, agency cost or legal cost incurred on an indemnity bases.
            </Typography>
            <Typography paragraph fontFamily='Inter'>
                2.1.9. In the event of a late payment, Litty may charge interest of 5% on the late payment. A new invoice will be issued for all additional charges.
            </Typography>
            <Typography variant="h6" gutterBottom fontFamily='Inter' sx={{ fontWeight: 'bold', mt: 2 }}>
                2.2. Full Payment Model
            </Typography>
            <Typography paragraph fontFamily='Inter'>
                2.2.1. Litty will charge the client the entire rate at the time of booking.
            </Typography>
            <Typography paragraph fontFamily='Inter'>
                2.2.2. Litty will remit 85% of the collected amount to the Venue within 5 business days of the event's conclusion.
            </Typography>
            <Typography paragraph fontFamily='Inter'>
                2.2.3. The Venue will make payment in full within 15 days after an invoice is received.
            </Typography>
            <Typography paragraph fontFamily='Inter'>
                2.2.4. Instructions for making payment of commission will be specified in the invoice for a given booking.
            </Typography>
            <Typography paragraph fontFamily='Inter'>
                2.2.5. A failure to pay an invoice within 10 days of the due date may result in your venue page being suspended and your credit history being affected.
            </Typography>
            <Typography paragraph fontFamily='Inter'>
                2.2.6. In the event we reasonably incur any cost (debt collection costs, legal advice, legal proceeding) to enforce our rights as a result of your breach of this agreement, including but not limited to recovery of any sums due, you will reimburse us such reasonable expense, agency cost or legal cost incurred on an indemnity bases.
            </Typography>
            <Typography paragraph fontFamily='Inter'>
                2.2.7. In the event of a late payment, Litty may charge interest of 5% on the late payment. A new invoice will be issued for all additional charges.
            </Typography>

            <Typography variant="h6" gutterBottom fontFamily='Inter' sx={{ fontWeight: 'bold', mt: 3 }}>
                3. Pricing Structure
            </Typography>
            <Typography paragraph fontFamily='Inter'>
                3.1. The rate for venue bookings may be based on a minimum spend amount, a hire rate, or a combination of both, as agreed upon by the Venue and Litty.
            </Typography>
            <Typography paragraph fontFamily='Inter'>
                3.2. If the client makes purchases beyond the minimum spend amount, Litty will not receive a commission on these additional purchases.
            </Typography>
            <Typography paragraph fontFamily='Inter'>
                3.3. The Venue must at all times quote a price on Litty which is accurate and matches the best available rate offered to the general public at the Venue or on the Venue's own website. If a customer or a Litty representative provides evidence of a lower price or better deal directly quoted by you or available to the general public at the Venue or on the Venue's own website for the same Venue, Room or Service booked through Litty, on the same booking terms, the Venue agrees to match the lower price and to refund the customer the difference between the price quoted through Litty and the lower price within 28 days.
            </Typography>

            <Typography variant="h6" gutterBottom fontFamily='Inter' sx={{ fontWeight: 'bold', mt: 3 }}>
                4. Taxes
            </Typography>
            <Typography paragraph fontFamily='Inter'>
                4.1. All rates and commissions mentioned in this contract are exclusive of applicable taxes.
            </Typography>
            <Typography paragraph fontFamily='Inter'>
                4.2. In the Full Payment Model, Litty will calculate and collect appropriate taxes from the client based on local tax regulations.
            </Typography>
            <Typography paragraph fontFamily='Inter'>
                4.3. Litty will remit the collected taxes to the Venue along with the 85% of the booking amount within 5 business days of the event's conclusion.
            </Typography>
            <Typography paragraph fontFamily='Inter'>
                4.4. The Venue is responsible for remitting all taxes collected to the appropriate tax authorities.
            </Typography>
            <Typography paragraph fontFamily='Inter'>
                4.5. In the Deposit Model, the Venue remains responsible for calculating, collecting, and remitting all applicable taxes.
            </Typography>
            <Typography paragraph fontFamily='Inter'>
                4.6. Litty will provide a detailed breakdown of taxes collected and remitted with each payment to the Venue in the Full Payment Model.
            </Typography>

            <Typography variant="h6" gutterBottom fontFamily='Inter' sx={{ fontWeight: 'bold', mt: 3 }}>
                6. Reporting and Payments
            </Typography>
            <Typography paragraph fontFamily='Inter'>
                6.1. The Venue agrees to provide accurate and timely reports of client spending for all bookings made through the Litty platform.
            </Typography>
            <Typography paragraph fontFamily='Inter'>
                6.2. Litty will provide statements detailing all bookings, commissions, and payments for the Venue by request.
            </Typography>

            <Typography variant="h6" gutterBottom fontFamily='Inter' sx={{ fontWeight: 'bold', mt: 3 }}>
                7. Term and Termination
            </Typography>
            <Typography paragraph fontFamily='Inter'>
                7.1. This contract shall remain in effect for one year from the date of signing and will automatically renew unless either party provides written notice of termination at least 30 days prior to the renewal date.
            </Typography>
            <Typography paragraph fontFamily='Inter'>
                7.2. Either party may terminate this contract with 60 days' written notice.
            </Typography>

            <Typography variant="h6" gutterBottom fontFamily='Inter' sx={{ fontWeight: 'bold', mt: 3 }}>
                8. Modifications
            </Typography>
            <Typography paragraph fontFamily='Inter'>
                8.1. Any modifications to this contract must be made in writing and agreed upon by both parties.
            </Typography>

            <Typography variant="h6" gutterBottom fontFamily='Inter' sx={{ fontWeight: 'bold', mt: 3 }}>
                9. Dispute Resolution
            </Typography>
            <Typography paragraph fontFamily='Inter'>
                9.1. In the event of any dispute arising out of or relating to this contract, the parties agree to attempt to resolve the dispute through good-faith negotiations for a period of 30 days from the date of written notice of the dispute.
            </Typography>
            <Typography paragraph fontFamily='Inter'>
                9.2. If the dispute is not resolved through negotiations, the parties agree to submit the dispute to mediation in accordance with New York State Unified Court System.
            </Typography>
            <Typography paragraph fontFamily='Inter'>
                9.3. If mediation is unsuccessful, any unresolved dispute arising out of or relating to this contract shall be settled by arbitration in accordance with New York State Unified Court System. The place of arbitration shall be New York, NY, USA. The arbitration shall be conducted in English by a single arbitrator.
            </Typography>
            <Typography paragraph fontFamily='Inter'>
                9.4. The decision of the arbitrator shall be final and binding on both parties, and judgment on the award rendered by the arbitrator may be entered in any court having jurisdiction thereof.
            </Typography>
            <Typography paragraph fontFamily='Inter'>
                9.5. Litty is not responsible for mediation or the enforcement of any obligations arising from an agreement between the Venue and the client.
            </Typography>

            <Typography variant="h6" gutterBottom fontFamily='Inter' sx={{ fontWeight: 'bold', mt: 3 }}>
                10. Insurance Requirements
            </Typography>
            <Typography paragraph fontFamily='Inter'>
                10.1. The Venue shall maintain, at its own expense, comprehensive general liability insurance, including but not limited to premises liability, personal injury liability, and liquor liability (if applicable) in accordance with local laws and requirements..
            </Typography>
            <Typography paragraph fontFamily='Inter'>
                10.2. Litty shall not be liable for any damages, injuries, or losses occurring on the Venue's premises or as a result of the Venue's operations, employees, or contractors.
            </Typography>
            <Typography paragraph fontFamily='Inter'>
                10.3. The Venue agrees to indemnify and hold Litty harmless from any claims, damages, or liabilities arising from the use of the Venue's premises or services.
            </Typography>

            <Typography variant="h6" gutterBottom fontFamily='Inter' sx={{ fontWeight: 'bold', mt: 3 }}>
                11. Cancellation Policy
            </Typography>
            <Typography paragraph fontFamily='Inter'>
                11.1. Each Venue shall define and maintain its own cancellation policy for bookings made through the Litty platform.
            </Typography>
            <Typography paragraph fontFamily='Inter'>
                11.2. The Venue's cancellation policy must be clearly communicated to Litty and displayed prominently on the Venue's listing on the Litty platform.
            </Typography>
            <Typography paragraph fontFamily='Inter'>
                11.3. In the event of a cancellation, the terms of the Venue's cancellation policy will apply, including any refunds or fees.
            </Typography>
            <Typography paragraph fontFamily='Inter'>
                11.4. In the event the Venue changes in cancelation policy, it shall not have a retrospective effect on any of its past bookings. Each booking will be canceled according to the booking cancelation policy selected by the Venue at the time that booking was made.
            </Typography>
            <Typography paragraph fontFamily='Inter'>
                11.4.1. If the Venue cancels the booking, the Venue is responsible for processing any refunds due to the client in accordance with the Venue's cancellation policy.
            </Typography>
            <Typography paragraph fontFamily='Inter'>
                11.4.2. If the client cancels the booking, the terms of the Venue's cancellation policy will apply, including any refunds or fees.
            </Typography>
            <Typography paragraph fontFamily='Inter'>
                11.4.3. Litty's commission shall be calculated based on the final amount charged to the client after any refunds or cancellation fees have been applied, in accordance with the Venue's cancellation policy.
            </Typography>
            <Typography paragraph fontFamily='Inter'>
                11.5. The Venue is responsible for processing any refunds due to clients in accordance with their cancellation policy.
            </Typography>

            <Typography variant="h6" gutterBottom fontFamily='Inter' sx={{ fontWeight: 'bold', mt: 3 }}>
                12. Force Majeure
            </Typography>
            <Typography paragraph fontFamily='Inter'>
                12.1. Neither party shall be liable for any failure or delay in performing their obligations under this contract if such failure or delay is caused by circumstances beyond their reasonable control, including but not limited to acts of God, natural disasters, war, terrorism, riots, strikes, lockouts, government actions, epidemics, pandemics, or other similar events ("Force Majeure Event").
            </Typography>
            <Typography paragraph fontFamily='Inter'>
                12.2. The party affected by a Force Majeure Event shall promptly notify the other party in writing, explaining the nature and expected duration of the Force Majeure Event.
            </Typography>
            <Typography paragraph fontFamily='Inter'>
                12.3. The affected party shall use reasonable efforts to mitigate the effects of the Force Majeure Event and resume performance of its obligations as soon as reasonably possible.
            </Typography>
            <Typography paragraph fontFamily='Inter'>
                12.4. If a Force Majeure Event continues for more than 90 days, either party may terminate this contract by providing written notice to the other party.
            </Typography>

            <Typography variant="h6" gutterBottom fontFamily='Inter' sx={{ fontWeight: 'bold', mt: 3 }}>
                13. Platform Integrity and Contact Information Policy
            </Typography>
            <Typography paragraph fontFamily='Inter'>
                13.1. The Venue agrees not to share, solicit, or encourage the sharing of contact information (including but not limited to phone numbers, email addresses, social media handles, or other direct contact methods) with clients with the intent to circumvent the Litty platform and avoid platform fees.
            </Typography>
            <Typography paragraph fontFamily='Inter'>
                13.2. The Venue acknowledges and agrees that Litty may monitor communications within the platform and may automatically filter or block messages containing contact information or other content that appears designed to circumvent the platform.
            </Typography>
            <Typography paragraph fontFamily='Inter'>
                13.3. Repeated attempts to circumvent the platform by sharing contact information may result in suspension or termination of the Venue's account on the Litty platform.
            </Typography>
            <Typography paragraph fontFamily='Inter'>
                13.4. This monitoring is in place to maintain the integrity of the platform and ensure compliance with these terms.
            </Typography>

            <Typography variant="h6" gutterBottom fontFamily='Inter' sx={{ fontWeight: 'bold', mt: 3 }}>
                14. Indemnification
            </Typography>
            <Typography paragraph fontFamily='Inter'>
                14.1. The Venue agrees to indemnify, defend, and hold harmless Litty, its officers, directors, employees, and agents from and against any and all claims, damages, liabilities, costs, and expenses (including reasonable attorneys' fees) arising out of or related to:
            </Typography>
            <Typography paragraph fontFamily='Inter'>
                a) The Venue's breach of any representation, warranty, or obligation under this contract;
            </Typography>
            <Typography paragraph fontFamily='Inter'>
                b) Any injury, loss, or damage occurring on the Venue's premises or as a result of the Venue's services;
            </Typography>
            <Typography paragraph fontFamily='Inter'>
                c) Any violation of law or regulation by the Venue;
            </Typography>
            <Typography paragraph fontFamily='Inter'>
                d) Any acts or omissions of the Venue's employees, contractors, or agents.
            </Typography>
            <Typography paragraph fontFamily='Inter'>
                14.2. Litty agrees to indemnify, defend, and hold harmless the Venue, its officers, directors, employees, and agents from and against any and all claims, damages, liabilities, costs, and expenses (including reasonable attorneys' fees) arising out of or related to:
            </Typography>
            <Typography paragraph fontFamily='Inter'>
                a) Litty's breach of any representation, warranty, or obligation under this contract;
            </Typography>
            <Typography paragraph fontFamily='Inter'>
                b) Any violation of law or regulation by Litty;
            </Typography>
            <Typography paragraph fontFamily='Inter'>
                14.3. The indemnified party shall promptly notify the indemnifying party in writing of any claim or action that may be subject to indemnification and shall cooperate fully in the defense of such claim or action.
            </Typography>
            <Typography paragraph fontFamily='Inter'>
                14.4. The indemnifying party shall have the right to control the defense and settlement of any claim or action subject to indemnification, provided that any settlement that imposes any liability or obligation on the indemnified party shall require the indemnified party's prior written consent.
            </Typography>
            <Typography paragraph fontFamily='Inter'>
                By using our Platform you acknowledge and agree that you (the Venue) have read, understood and agreed to the Terms set out below and confirm your agreement to enter into and comply with a legally binding contract with Litty on these Terms.
            </Typography>
        </Paper>
    )
}   